// The star rules, shared so the Worker that closes days and the screens that show stars agree.
// A closed day earns a star when its closing score is at or above the kid's goal; consecutive
// star days make a streak, and every seven in a row is a badge.

import { nextDay } from "./time";

export const DEFAULT_GOAL = 90;

/**
 * The most days one read of closed days covers, and how far back auto-close looks, so a
 * long-idle household cannot stall a request: a year and a bit.
 */
export const MAX_DAYS = 400;
export const MIN_GOAL = 1;
export const MAX_GOAL = 100;

/** Star days in a row that make a badge (the bigger reward). */
export const MILESTONE = 7;

/** Settings steps the goal by 5 between 50 and 100; the server takes any whole 1..100. */
export const GOAL_STEP = 5;
export const GOAL_STEPPER_MIN = 50;

/** True for a whole number from 1 to 100. */
export function isGoal(n: unknown): n is number {
  return typeof n === "number" && Number.isInteger(n) && n >= MIN_GOAL && n <= MAX_GOAL;
}

export function starFor(closingScore: number, goal: number): boolean {
  return closingScore >= goal;
}

/**
 * Whether today was closed and the score has since crossed the goal the other way, so closing
 * again would change the star.
 */
export function closeOutdated(today: { closed: boolean; star: boolean | null }, score: number, goal: number): boolean {
  return today.closed && today.star !== starFor(score, goal);
}

/**
 * The goal one stepper press away, snapped to a multiple of 5 so a goal set to 87 elsewhere
 * goes to 85 or 90. Returns `goal` unchanged at the stepper's ends, so the button can disable.
 */
export function stepGoal(goal: number, dir: 1 | -1): number {
  if (dir < 0) {
    return goal <= GOAL_STEPPER_MIN ? goal : Math.max(GOAL_STEPPER_MIN, Math.ceil(goal / GOAL_STEP) * GOAL_STEP - GOAL_STEP);
  }
  return goal >= MAX_GOAL ? goal : Math.min(MAX_GOAL, Math.floor(goal / GOAL_STEP) * GOAL_STEP + GOAL_STEP);
}

type StarDay = { day: string; star: boolean };

/**
 * The length of the run of star days in a row at each closed day, oldest first. A day without a
 * star, or a day missing from the list, ends a run. Streak and badges both read this, so they
 * can never disagree about what counts as "in a row".
 */
function runs(days: readonly StarDay[]): number[] {
  const sorted = [...days].sort((a, b) => (a.day < b.day ? -1 : a.day > b.day ? 1 : 0));
  const out: number[] = [];
  let run = 0;
  let prev = "";
  for (const d of sorted) {
    run = d.star ? (run > 0 && d.day === nextDay(prev) ? run + 1 : 1) : 0;
    out.push(run);
    prev = d.day;
  }
  return out;
}

/**
 * Star days in a row ending at the latest closed day: yesterday (every day before today closes
 * by itself at midnight), or today once it is closed. Today still open counts for nothing and
 * breaks nothing; today closed without a star makes the streak 0.
 */
export function streakFrom(days: readonly StarDay[], today: string): number {
  return runs(days.filter((d) => d.day <= today)).at(-1) ?? 0;
}

/** How many times a run of star days reached 7, 14, 21… A missing day ends a run. */
export function badgesFrom(days: readonly StarDay[]): number {
  return runs(days).filter((run) => run > 0 && run % MILESTONE === 0).length;
}
